import React, { memo, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import cn from 'classnames';

import { Button, Icons, Modal, TextArea, Tooltip } from '@components/common';
import FilesList from '@components/common/FilesList/FilesList';
import RadioButton from '@components/common/Form/RadioButton';
import AttachDocuments from '@components/ModalComponents/AssetForm/Attributes/Status/AttachAssetDocuments';

import { uuid } from '@utils/common';

import { IAssetFile } from '@interfaces/assets/asset';
import { IAssetFormValues, TStatusOptionBool, TStatusOptionEnum } from '@interfaces/components/form';

import { TAssetSiteA11yStatus, TBackhaulA11yStatus, TPowerSupplyA11yStatus } from '@enums/map';

import { MODAL_WIDTH_580 } from '@constants/modals';

import styles from './Status.module.scss';

interface IStatusProps {
	radioValue: TBackhaulA11yStatus | TPowerSupplyA11yStatus | TAssetSiteA11yStatus;
	radioName: string;
	setValue: UseFormSetValue<IAssetFormValues>;
	statusOptions: Array<TStatusOptionEnum | TStatusOptionBool>;
	register: UseFormRegister<FieldValues>;
	errorMessage?: string;
	detailsName: string;
	isStatusType: boolean;
	fileFieldName: string;
	required: boolean;
	files: Array<IAssetFile>;
}

const Status = ({
	radioValue,
	radioName,
	setValue,
	statusOptions,
	register,
	errorMessage,
	detailsName,
	isStatusType,
	fileFieldName,
	files,
	required,
}: IStatusProps) => {
	const [isOpenUploadFilesModal, setIsOpenUploadFilesModal] = useState(false);

	const toggleUploadFilesModal = () => setIsOpenUploadFilesModal(prevState => !prevState);

	const handleUploadFiles = (uploadedFiles: Array<IAssetFile>) => {
		setValue(fileFieldName as any, [...files, ...uploadedFiles]);
	};

	const handleRemoveFile = (fileId: string) =>
		setValue(fileFieldName as any, files.filter(file => file.id !== fileId));

	return (
		<div className={styles.statusContainer}>
			<Modal
				isOpen={isOpenUploadFilesModal}
				isScrollableContent
				onClose={toggleUploadFilesModal}
				title='Upload file'
				width={MODAL_WIDTH_580}
			>
				<AttachDocuments
					handleUploadFiles={handleUploadFiles}
					toggleUploadFilesModal={toggleUploadFilesModal}
				/>
			</Modal>
			<div className={cn(styles.radioButtons, { [styles.row]: !isStatusType })}>
				{statusOptions.map(option => {
					const onChangeStatus = () => setValue(radioName as any, option.value);

					return (
						<RadioButton
							key={uuid()}
							color={option.color}
							defaultChecked={option.value === radioValue}
							id={option.value + radioName + option.color}
							label={option.label}
							name={radioName}
							onChange={onChangeStatus}
							withUseForms
						/>
					);
				})
				}
			</div>
			<TextArea
				className={styles.details}
				errorMessage={errorMessage}
				label='Details'
				name={detailsName}
				placeholder='Enter description of the details'
				register={register}
				required={required}
				rows={3}
				withUseForms
			/>
			<div className={styles.button}>
				<Tooltip
					content={
						<>
							Acceptable size: 10MB
							<br />
							Acceptable extensions: doc, docx, xls, xlsx, txt, ppt, pptx, jpg, png, pdf
						</>
					}
					placement='top-start'
				>
					<div>
						<Button
							icon={Icons.plus()}
							onClick={toggleUploadFilesModal}
							text='Add attachment'
							withoutBackground
						/>
					</div>
				</Tooltip>
			</div>
			<FilesList
				files={files}
				handleRemoveFile={handleRemoveFile}
			/>
		</div>
	);
};

export default memo(Status);
